/**
 * /snt/invoice/plaintext 敏感字段解密
 */
import util from 'lib/util'

export const OPEN_EYE_ICON = 'https://cdn.kuaidi100.com/images/snt/wxapp/open-eye.png'
export const CLOSE_EYE_ICON = 'https://cdn.kuaidi100.com/images/snt/wxapp/close-eye.png'

function isPlaintextSuccess(res) {
  return res && (String(res.status) === '200' || res.success === true)
}

/**
 * 解析明文接口返回字段
 * @param {object} res
 * @param {string[]} fieldName
 * @returns {Record<string, string>}
 */
export function parsePlaintextFields(res, fieldName) {
  const fields = Array.isArray(fieldName) ? fieldName : [fieldName]
  const result = {}
  fields.forEach((key) => {
    result[key] = ''
  })
  if (!isPlaintextSuccess(res)) {
    return result
  }
  const data = res.data
  if (data == null) {
    return result
  }
  if (Array.isArray(data)) {
    data.forEach((item) => {
      if (!item || !item.field || item.value == null) {
        return
      }
      result[item.field] = String(item.value).trim()
    })
    return result
  }
  if (typeof data === 'object') {
    fields.forEach((key) => {
      if (data[key] != null && String(data[key]).trim()) {
        result[key] = String(data[key]).trim()
      }
    })
  }
  return result
}

/**
 * 解析解密所需 userId（同步：订单字段 / cookie / session）
 * @param {object} orderDetail
 * @returns {string}
 */
export function resolvePlaintextUserId(orderDetail = {}) {
  const candidates = ['userId', 'userid', 'sntId', 'sntUserId', 'dbId', 'createUserId']
  for (let i = 0; i < candidates.length; i++) {
    const val = orderDetail[candidates[i]]
    if (val != null && val !== '' && String(val) !== '0') {
      return String(val)
    }
  }
  return util.getcookie('userId')
    || util.getcookie('platformUserId')
    || util.getcookie('USERID')
    || util.getSession('userId')
    || util.getSession('USERID')
    || util.getSession('platformUserId')
    || ''
}

/**
 * 异步解析解密 userId：本地没有时请求 getuserinfo
 * @param {object} http $http 实例
 * @param {object} [orderDetail]
 * @returns {Promise<string>}
 */
export function fetchPlaintextUserId(http, orderDetail = {}) {
  const cached = resolvePlaintextUserId(orderDetail)
  if (cached) {
    return Promise.resolve(cached)
  }
  if (!http || typeof http.get !== 'function') {
    return Promise.resolve('')
  }
  return http
    .get('/user/userapi.do?method=getuserinfo', {
      handleFail: false
    })
    .then((data) => {
      const user = (data && data.user)
        || (data && Array.isArray(data.data) && data.data[0])
        || (data && data.data && typeof data.data === 'object' ? data.data : null)
        || {}
      const userId = user.id || user.userId || user.userid || ''
      if (userId != null && userId !== '' && String(userId) !== '0') {
        return String(userId)
      }
      return ''
    })
    .catch(() => '')
}

/**
 * 组装订单详情解密请求体（与管家小程序 ORDER_DETAIL 一致）
 * @param {string|number} userId
 * @param {string|number} expId
 * @param {string[]} fieldName
 * @returns {object}
 */
export function buildOrderDetailPlaintextParam(userId, expId, fieldName) {
  return {
    bizType: 'ORDER_DETAIL',
    userId: userId != null && String(userId).trim() ? String(userId).trim() : '',
    bizId: expId != null && String(expId).trim() ? String(expId).trim() : '',
    fieldName: Array.isArray(fieldName) ? fieldName : []
  }
}

/**
 * 邀请寄件订单详情手机号明文
 * @param {object} http $http 实例
 * @param {string|number} userId
 * @param {string|number} expId
 * @param {string[]} fieldName
 * @returns {Promise<Record<string, string>>}
 */
export function fetchSntInviteOrderPlaintext(http, userId, expId, fieldName) {
  const fields = Array.isArray(fieldName) ? fieldName : [fieldName]
  const empty = {}
  fields.forEach((key) => {
    empty[key] = ''
  })
  if (!userId || !expId || !fields.length) {
    return Promise.resolve(empty)
  }
  const resolvedUserId = String(userId).trim()
  const data = buildOrderDetailPlaintextParam(resolvedUserId, expId, fields)
  const headers = {
    'X-USER-ID': resolvedUserId
  }
  return http
    .jsonPost('/snt/invoice/plaintext', data, { headers })
    .then((res) => parsePlaintextFields(res, fields))
    .catch(() => empty)
}

/**
 * 从解密结果中取手机号明文
 * @param {object} fields
 * @param {'send'|'rec'} side
 * @returns {string}
 */
export function pickOrderPhonePlaintext(fields, side) {
  if (!fields) {
    return ''
  }
  if (side === 'send') {
    return fields.sendMobile || fields.sendTel || fields.sendPhone || ''
  }
  if (side === 'rec') {
    return fields.recMobile || fields.recTel || fields.recPhone || ''
  }
  return ''
}

/**
 * 从解密结果中取详细地址明文（不含省市区）
 * @param {object} fields
 * @param {'send'|'rec'} side
 * @returns {string}
 */
export function pickOrderAddrPlaintext(fields, side) {
  if (!fields) {
    return ''
  }
  if (side === 'send') {
    return fields.sendAddr || fields.sendaddr2 || fields.sendaddr || ''
  }
  if (side === 'rec') {
    return fields.recAddr || fields.recaddr2 || fields.recaddr || ''
  }
  return ''
}

/**
 * 判断订单电话字段是否有值
 * @param {*} value
 * @returns {boolean}
 */
export function hasOrderPhoneValue(value) {
  return value != null && value !== '' && value !== 'null'
}

/**
 * 寄件人解密 fieldName
 * @param {object} orderDetail
 * @returns {string[]}
 */
export function getSendDecryptFieldNames(orderDetail = {}) {
  const fields = ['sendAddr']
  if (hasOrderPhoneValue(orderDetail.sendPhone) || hasOrderPhoneValue(orderDetail.sendmobile)) {
    fields.push('sendMobile')
  } else if (hasOrderPhoneValue(orderDetail.sendtel)) {
    fields.push('sendTel')
  }
  return fields
}

/**
 * 收件人解密 fieldName
 * @param {object} orderDetail
 * @returns {string[]}
 */
export function getRecDecryptFieldNames(orderDetail = {}) {
  const fields = ['recAddr']
  if (hasOrderPhoneValue(orderDetail.recPhone) || hasOrderPhoneValue(orderDetail.recmobile)) {
    fields.push('recMobile')
  } else if (hasOrderPhoneValue(orderDetail.rectel)) {
    fields.push('recTel')
  }
  return fields
}

/**
 * 解密结果是否包含手机号或地址
 * @param {object} fields
 * @param {'send'|'rec'} side
 * @returns {boolean}
 */
export function hasOrderPlaintext(fields, side) {
  return !!(pickOrderPhonePlaintext(fields, side) || pickOrderAddrPlaintext(fields, side))
}
